import { PreJobCheckListV2, VehicleType } from "../../../generated/nest-graphql";
import { formatStoredMiles } from "../formatMiles.utils";
import { vehicleNotes } from "../EditVehicleDialog/editVehicleForm.utils";
import { rearDrumBrakes } from "./rearDrumBrakes.utils";
import { hasSpecValue } from "./specValue.utils";

export enum VehicleSpecLabel {
  MILEAGE = "Mileage (last known)",
  TRIM = "Trim",
  REAR_DRUM_BRAKES = "Rear drum brakes",
  PARKING_BRAKE = "Parking brake",
  FRONT_ROTOR_DISCARD = "Front rotor discard",
  REAR_ROTOR_DISCARD = "Rear rotor discard",
  WHEEL_TORQUE = "Wheel torque",
  OIL_CAPACITY = "Oil capacity",
  BATTERY_CCA = "Battery CCA",
  NOTES = "Notes",
}

export type VehicleSpecRow = { label: VehicleSpecLabel; value: string | null; detail?: string | null };

const withUnit = (value: number | null | undefined, unit: string) => (hasSpecValue(value) ? `${value} ${unit}` : null);

/** Torque is stored as typed; a bare number is the usual foot-pounds. */
const torque = (value?: string | null) => {
  const text = value?.trim();
  if (!text || !hasSpecValue(text)) return null;
  return /^\d+(\.\d+)?$/.test(text) ? `${text} ft-lb` : text;
};

const textValue = (value?: string | null) => (hasSpecValue(value) ? (value as string).trim() : null);

/** Every spec the record can hold, in a fixed order; a missing one has a null value rather than no row. */
export const vehicleSpecRows = (
  vehicle?: VehicleType | null,
  inspection?: PreJobCheckListV2 | null,
  /** Where the record's mileage came from, shown under it. */
  mileageSource?: string | null
): VehicleSpecRow[] => {
  const info = vehicle?.vehicleTechnicalInfo;
  return [
    {
      label: VehicleSpecLabel.MILEAGE,
      value: hasSpecValue(vehicle?.odometer) ? formatStoredMiles(vehicle?.odometer) : null,
      detail: hasSpecValue(vehicle?.odometer) ? mileageSource ?? null : null,
    },
    { label: VehicleSpecLabel.TRIM, value: textValue(vehicle?.subModel) },
    { label: VehicleSpecLabel.REAR_DRUM_BRAKES, value: rearDrumBrakes(vehicle, inspection) },
    { label: VehicleSpecLabel.PARKING_BRAKE, value: textValue(info?.parkingBrake) },
    { label: VehicleSpecLabel.FRONT_ROTOR_DISCARD, value: withUnit(info?.frontRotorsDiscardThicknessInches, "in") },
    { label: VehicleSpecLabel.REAR_ROTOR_DISCARD, value: withUnit(info?.rearRotorsDiscardThicknessInches, "in") },
    { label: VehicleSpecLabel.WHEEL_TORQUE, value: torque(info?.wheelTorque) },
    { label: VehicleSpecLabel.OIL_CAPACITY, value: withUnit(info?.oilCapacityQuarts, "qt") },
    { label: VehicleSpecLabel.BATTERY_CCA, value: withUnit(vehicle?.CCA, "CCA") },
    { label: VehicleSpecLabel.NOTES, value: vehicleNotes(vehicle) },
  ];
};

// Notes always shows, so a tech can add them.
export const shownSpecRows = (rows: VehicleSpecRow[]): VehicleSpecRow[] =>
  rows.filter((row) => row.label === VehicleSpecLabel.NOTES || hasSpecValue(row.value));
